import { Subject, distinctUntilChanged, filter, fromEvent, map, merge, sampleTime } from "rxjs";
import { HTMLElementAttribute, HTMLElementEventType } from "../../../common/components/misc.enums";
import { attachHandlers } from "../formHelpers/formHelpers";
import { sendInteractionMetrics } from "../formHelpers/metricsHelper";
import { UIType } from "../../../common/components/cdpaRequest";
var KeyboardCode;
(function (KeyboardCode) {
    KeyboardCode["Backspace"] = "Backspace";
    KeyboardCode["ArrowLeft"] = "ArrowLeft";
    KeyboardCode["ArrowRight"] = "ArrowRight";
    KeyboardCode["ArrowUp"] = "ArrowUp";
    KeyboardCode["ArrowDown"] = "ArrowDown";
    KeyboardCode["Tab"] = "Tab";
    KeyboardCode["Enter"] = "Enter";
    KeyboardCode["Delete"] = "Delete";
    KeyboardCode["NumpadBackspace"] = "NumpadBackspace";
    KeyboardCode["Space"] = "Space";
})(KeyboardCode || (KeyboardCode = {}));
export class CdpaMultiFieldInput {
    constructor(inputsClassName, wrapperClassName, labelClassName, isAlphanumeric = false) {
        this.inputsClassName = inputsClassName;
        this.wrapperClassName = wrapperClassName;
        this.labelClassName = labelClassName;
        this.isAlphanumeric = isAlphanumeric;
        this.inputs = document.querySelectorAll(`.${this.inputsClassName}`);
        this.emailCodeInputParent = document.getElementById(this.wrapperClassName);
        this.fullCodeInput = document.getElementById("full-code");
        this.inputLabel = document.querySelector(`.${this.labelClassName}`);
        this.onValidPasteSubject = new Subject();
        this.onValidPaste$ = this.onValidPasteSubject.asObservable();
        attachHandlers([
            [fromEvent(this.emailCodeInputParent, HTMLElementEventType.Input), this.onInputEvent.bind(this)],
            [
                fromEvent(this.inputs[0], HTMLElementEventType.Click),
                () => {
                    // Trigger focus, so that on ios clicking on a focused input will show the keyboard
                    if (document.activeElement === this.inputs[0]) {
                        this.inputs[0].focus();
                    }
                }
            ]
        ]);
        const isAnyFocusedSubject = new Subject();
        this.inputs.forEach((input, currIndex) => {
            const focusEvent$ = fromEvent(input, HTMLElementEventType.Focus);
            const blurEvent$ = fromEvent(input, HTMLElementEventType.Blur);
            merge(focusEvent$.pipe(map(() => true)), blurEvent$.pipe(map(() => false)))
                .pipe(distinctUntilChanged())
                .subscribe(isAnyFocusedSubject);
            attachHandlers([
                [fromEvent(input, HTMLElementEventType.Paste), (event) => this.onInputPasteEvent(event, currIndex)],
                [
                    fromEvent(input, HTMLElementEventType.Input),
                    (event) => this.onInputInputEvent(event, currIndex, input)
                ],
                [fromEvent(input, HTMLElementEventType.Keydown), (event) => this.onInputKeydown(event, input)],
                [focusEvent$, () => this.onInputFocus(input)],
                [blurEvent$, (event) => this.onInputBlur(event)]
            ]);
        });
        this.focusFirst();
        this.sendMetricsOnFocus(isAnyFocusedSubject);
        if (this.emailCodeInputParent.getAttribute(HTMLElementAttribute.DataLabel)) {
            this.setValidity(false);
        }
    }
    validate() {
        const values = [];
        this.inputs.forEach((input) => {
            values.push(input.value);
        });
        const allowedKeysRegex = this.isAlphanumeric ? /^[A-Za-z0-9]{8}$/ : /^[0-9]{6}$/;
        const isValid = values.join("").search(allowedKeysRegex) === 0;
        this.setValidity(isValid);
        return isValid;
    }
    onInputFocus(input) {
        // If all the inputs are empty, user can only focus the first input
        if (input !== this.inputs[0] && this.concatCode() === "") {
            this.focusFirst();
            return;
        }
        this.setValidity(true);
    }
    concatCode() {
        let text = "";
        this.inputs.forEach((input) => {
            text += input.value;
        });
        return text;
    }
    onInputPasteEvent(event, currentIndex) {
        event.preventDefault();
        const clipboardText = event.clipboardData?.getData("text");
        if (clipboardText === undefined) {
            return;
        }
        this.setValueInMultipleFields(clipboardText, currentIndex);
    }
    onInputInputEvent(event, currentIndex, input) {
        const value = event.data ?? "";
        if (value.length > 1) {
            this.setValueInMultipleFields(value, currentIndex);
            event.stopPropagation();
        }
        else if (CdpaMultiFieldInput.isDecimalOrNegativeNumber(value)) {
            event.stopPropagation();
            input.value = "";
        }
    }
    setValueInMultipleFields(value, currentIndex) {
        const text = value.trim();
        if (!text) {
            return;
        }
        this.inputs.forEach((input, index) => {
            const character = text[index - currentIndex];
            if (index >= currentIndex && character) {
                input.focus();
                input.value = character.toUpperCase() || "";
            }
        });
        this.updateFullCodeInput();
        if (this.validate()) {
            this.onValidPasteSubject.next();
        }
    }
    onInputBlur(event) {
        // relatedTarget is the new focused element. If focus is moved to another input field, do not yet validate
        if (this.concatCode() === "" ||
            (event.relatedTarget !== null && event.relatedTarget.classList.contains(this.inputsClassName))) {
            return;
        }
        this.validate();
    }
    setValidity(isValid) {
        this.updateValidityClass(isValid);
        this.setInputLabel(isValid);
    }
    updateValidityClass(isValid) {
        const invalidOTP = "email-code__input--invalid";
        Object.values(this.emailCodeInputParent.children).forEach((input) => isValid ? input.classList.remove(invalidOTP) : input.classList.add(invalidOTP));
    }
    setInputLabel(shouldHideLabel) {
        const inputLabelHidden = `${this.labelClassName}--hidden`;
        shouldHideLabel
            ? this.inputLabel.classList.add(inputLabelHidden)
            : this.inputLabel.classList.remove(inputLabelHidden);
    }
    focusFirst() {
        this.inputs[0].focus();
    }
    onInputEvent(event) {
        const target = event.target;
        CdpaMultiFieldInput.tryToFocusNext(target);
        this.updateFullCodeInput();
    }
    updateFullCodeInput() {
        this.fullCodeInput.value = this.concatCode();
    }
    onInputKeydown(event, input) {
        // Allowed keys with default behaviour
        const allowedKeysRegex = this.isAlphanumeric ? /^[A-Za-z0-9]$/ : /^[0-9]$/;
        const isAllowedKey = event.key.search(allowedKeysRegex) === 0;
        // This is to make sure input field never contains more than one character
        if (isAllowedKey) {
            input.value = "";
            return;
        }
        if (event.code === KeyboardCode.Tab ||
            event.code === KeyboardCode.Enter ||
            event.metaKey ||
            event.ctrlKey ||
            event.altKey) {
            return;
        }
        if ([KeyboardCode.Backspace, KeyboardCode.Delete, KeyboardCode.NumpadBackspace].includes(event.code) ||
            event.key === KeyboardCode.Backspace // Handle backspace on Android
        ) {
            event.preventDefault();
            if (input.value) {
                input.value = "";
            }
            else {
                CdpaMultiFieldInput.tryToFocusPrevious(input);
                CdpaMultiFieldInput.tryToErasePrevious(input);
            }
            return;
        }
        if (event.code === KeyboardCode.ArrowLeft) {
            event.preventDefault();
            CdpaMultiFieldInput.tryToFocusPrevious(input);
            return;
        }
        if (event.code === KeyboardCode.ArrowRight) {
            event.preventDefault();
            CdpaMultiFieldInput.tryToFocusNext(input);
            return;
        }
        event.preventDefault();
        event.stopPropagation();
    }
    sendMetricsOnFocus(isAnyFocusedSubject) {
        isAnyFocusedSubject
            .pipe(sampleTime(100), distinctUntilChanged(), filter((value) => value))
            .subscribe(() => {
            sendInteractionMetrics(this.fullCodeInput, UIType.Input);
        });
    }
    static tryToFocusNext(input) {
        const nextInput = CdpaMultiFieldInput.getNextInputElement(input);
        if (nextInput) {
            nextInput.focus();
        }
    }
    static getNextInputElement(input) {
        let currentElement = input;
        while (currentElement?.nextElementSibling) {
            if (currentElement.nextElementSibling instanceof HTMLInputElement) {
                return currentElement.nextElementSibling;
            }
            currentElement = currentElement.nextElementSibling;
        }
        return null;
    }
    static getPreviousInputElement(input) {
        let currentElement = input;
        while (currentElement?.previousElementSibling) {
            if (currentElement.previousElementSibling instanceof HTMLInputElement) {
                return currentElement.previousElementSibling;
            }
            currentElement = currentElement.previousElementSibling;
        }
        return null;
    }
    static tryToFocusPrevious(input) {
        const previousElement = this.getPreviousInputElement(input);
        if (previousElement) {
            previousElement.focus();
        }
    }
    static tryToErasePrevious(input) {
        const previousElement = this.getPreviousInputElement(input);
        if (previousElement) {
            previousElement.value = "";
        }
    }
    static isDecimalOrNegativeNumber(value) {
        return [".", ",", "-"].includes(value);
    }
}
